import React from 'react';
import { motion } from 'framer-motion';

const TechnicalSpecs = ({ specifications, category }) => {
  const renderBearingSpecs = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Hauptabmessungen</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Innendurchmesser (d):</span>
            <span className="font-medium">{specifications.inner_diameter_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Außendurchmesser (D):</span>
            <span className="font-medium">{specifications.outer_diameter_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Breite (B):</span>
            <span className="font-medium">{specifications.width_mm} mm</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Belastbarkeit</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Dyn. Tragzahl (C):</span>
            <span className="font-medium">{specifications.dynamic_load_rating_n?.toLocaleString()} N</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Stat. Tragzahl (C₀):</span>
            <span className="font-medium">{specifications.static_load_rating_n?.toLocaleString()} N</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Grenzdreh. (Fett):</span>
            <span className="font-medium">{specifications.limiting_speed_grease_rpm?.toLocaleString()} min⁻¹</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Ausführung</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Lagerluft:</span>
            <span className="font-medium">{specifications.clearance_class || 'Normal'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Dichtung:</span>
            <span className="font-medium">{specifications.seal_type || 'Offen'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Käfig:</span>
            <span className="font-medium">{specifications.cage_material}</span>
          </div>
          {specifications.attributes?.contact_angle_degrees && (
            <div className="flex justify-between">
              <span className="text-gray-600">Druckwinkel:</span>
              <span className="font-medium">{specifications.attributes.contact_angle_degrees}°</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderLinearSpecs = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Abmessungen</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Baugröße:</span>
            <span className="font-medium">{specifications.size}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Höhe:</span>
            <span className="font-medium">{specifications.dimensions?.height_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Breite:</span>
            <span className="font-medium">{specifications.dimensions?.width_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Länge:</span>
            <span className="font-medium">{specifications.dimensions?.length_mm} mm</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Tragfähigkeit</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Dyn. Tragzahl:</span>
            <span className="font-medium">{specifications.load_ratings?.dynamic_C_N?.toLocaleString()} N</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Stat. Tragzahl:</span>
            <span className="font-medium">{specifications.load_ratings?.static_C0_N?.toLocaleString()} N</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">M₀ₓ:</span>
            <span className="font-medium">{specifications.load_ratings?.static_moments_Nm?.M0x} Nm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">M₀ᵧ:</span>
            <span className="font-medium">{specifications.load_ratings?.static_moments_Nm?.M0y} Nm</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Ausführung</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Bauform:</span>
            <span className="font-medium">{specifications.design}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Genauigkeit:</span>
            <span className="font-medium">{specifications.accuracy_class}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Vorspannung:</span>
            <span className="font-medium">{specifications.preload_class}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSealSpecs = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Abmessungen</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Innendurchmesser:</span>
            <span className="font-medium">{specifications.inner_diameter_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Außendurchmesser:</span>
            <span className="font-medium">{specifications.outer_diameter_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Breite:</span>
            <span className="font-medium">{specifications.width_mm} mm</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Material</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Werkstoff:</span>
            <span className="font-medium">{specifications.material}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Shore-Härte:</span>
            <span className="font-medium">{specifications.material_shore_hardness}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Min. Temperatur:</span>
            <span className="font-medium">{specifications.temperature_range?.min_celsius}°C</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Max. Temperatur:</span>
            <span className="font-medium">{specifications.temperature_range?.max_celsius}°C</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Bauform</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Staublippe:</span>
            <span className="font-medium">{specifications.design_features?.has_dust_lip ? 'Ja' : 'Nein'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Außenmantel:</span>
            <span className="font-medium">{specifications.design_features?.outer_case}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Drallrichtung:</span>
            <span className="font-medium">{specifications.design_features?.helix_direction}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBeltSpecs = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Abmessungen</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Profil:</span>
            <span className="font-medium">{specifications.profile}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Richtlänge (Ld):</span>
            <span className="font-medium">{specifications.reference_length_ld_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Außenlänge (La):</span>
            <span className="font-medium">{specifications.calculated_lengths?.outer_length_la_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Innenlänge (Li):</span>
            <span className="font-medium">{specifications.calculated_lengths?.inner_length_li_mm} mm</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Querschnitt</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Kopfbreite:</span>
            <span className="font-medium">{specifications.cross_section?.top_width_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Höhe:</span>
            <span className="font-medium">{specifications.cross_section?.height_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Keilwinkel:</span>
            <span className="font-medium">{specifications.cross_section?.angle_degrees}°</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Leistung</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Max. Leistung:</span>
            <span className="font-medium">{specifications.performance?.max_power_kw} kW</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Min. Scheibendurchmesser:</span>
            <span className="font-medium">{specifications.performance?.min_pulley_diameter_mm} mm</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Zugstrang:</span>
            <span className="font-medium">{specifications.construction?.tensile_member}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSpecs = () => {
    switch (category) {
      case 'EC000030':
      case 'EC000031':
        return renderBearingSpecs();
      case 'EC000924':
        return renderLinearSpecs();
      case 'EC002306':
        return renderSealSpecs();
      case 'EC001234':
        return renderBeltSpecs();
      default:
        return (
          <div className="text-center text-gray-600">
            Technische Daten nicht verfügbar
          </div>
        );
    }
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <h2 className="text-xl font-bold text-gray-900 mb-6">Technische Spezifikationen</h2>
      {renderSpecs()}
    </motion.div>
  );
};

export default TechnicalSpecs;